import React from 'react';
import Icon from '../../../components/AppIcon';

const StreakTracker = () => {
  const currentStreak = 12;
  const longestStreak = 28;
  const streakGoal = 30;
  
  // Generate last 14 days for streak visualization
  const generateStreakDays = () => {
    const days = [];
    const today = new Date();
    
    for (let i = 13; i >= 0; i--) {
      const date = new Date(today);
      date?.setDate(date?.getDate() - i);
      
      // Mock streak data - assume user has been active for last 12 days
      const isActive = i <= 11;
      const dayName = date?.toLocaleDateString('es-ES', { weekday: 'short' });
      
      days?.push({
        date: date?.getDate(),
        dayName: dayName?.charAt(0)?.toUpperCase() + dayName?.slice(1),
        isActive,
        isToday: i === 0
      });
    }
    
    return days;
  };

  const streakDays = generateStreakDays();
  const streakProgress = (currentStreak / streakGoal) * 100;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-semibold text-lg text-foreground">
          Racha de Rescates
        </h3>
        <div className="flex items-center space-x-2 text-accent">
          <Icon name="Flame" size={20} />
          <span className="font-bold text-lg">{currentStreak}</span>
        </div>
      </div>
      {/* Current Streak Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="text-center p-4 bg-muted/30 rounded-lg">
          <div className="text-2xl font-bold text-accent mb-1">{currentStreak}</div>
          <div className="text-sm text-muted-foreground">Racha Actual</div>
        </div>
        <div className="text-center p-4 bg-muted/30 rounded-lg">
          <div className="text-2xl font-bold text-warning mb-1">{longestStreak}</div>
          <div className="text-sm text-muted-foreground">Racha Más Larga</div>
        </div>
      </div>
      {/* Streak Progress to Goal */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-foreground">
            Progreso hacia meta de {streakGoal} días
          </span>
          <span className="text-sm text-muted-foreground">
            {currentStreak}/{streakGoal}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-accent to-warning h-3 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(streakProgress, 100)}%` }}
          ></div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          ¡Solo {streakGoal - currentStreak} días más para conseguir el logro "Maestro de la Constancia"!
        </p>
      </div>
      {/* Daily Streak Visualization */}
      <div className="mb-6">
        <h4 className="font-medium text-foreground mb-3">Últimos 14 días</h4>
        <div className="grid grid-cols-7 gap-2">
          {streakDays?.map((day, index) => (
            <div key={index} className="text-center">
              <div className="text-xs text-muted-foreground mb-1">
                {day?.dayName}
              </div>
              <div 
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-medium transition-all ${
                  day?.isActive 
                    ? day?.isToday 
                      ? 'bg-accent text-accent-foreground ring-2 ring-accent/50' 
                      : 'bg-success text-success-foreground' :'bg-muted text-muted-foreground'
                }`}
              >
                {day?.isActive ? (
                  <Icon name="Check" size={12} />
                ) : (
                  day?.date
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Streak Milestones */}
      <div className="space-y-3">
        <h4 className="font-medium text-foreground">Próximos hitos</h4>
        
        <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
          <div className="w-8 h-8 bg-warning/20 rounded-full flex items-center justify-center">
            <Icon name="Target" size={16} color="var(--color-warning)" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-sm text-foreground">15 días consecutivos</div>
            <div className="text-xs text-muted-foreground">Logro "Dedicación Semanal"</div>
          </div>
          <div className="text-xs text-muted-foreground">
            {15 - currentStreak > 0 ? `${15 - currentStreak} días` : '¡Conseguido!'}
          </div>
        </div>

        <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
          <div className="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center">
            <Icon name="Award" size={16} color="var(--color-accent)" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-sm text-foreground">30 días consecutivos</div>
            <div className="text-xs text-muted-foreground">Logro "Maestro de la Constancia"</div>
          </div>
          <div className="text-xs text-muted-foreground">
            {streakGoal - currentStreak} días
          </div>
        </div>
      </div>
      {/* Motivational Message */}
      <div className="mt-6 p-4 bg-gradient-to-r from-success/10 to-accent/10 rounded-lg border border-success/20">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={20} color="var(--color-success)" />
          <div>
            <h5 className="font-medium text-foreground text-sm mb-1">
              ¡Mantén el impulso!
            </h5>
            <p className="text-xs text-muted-foreground">
              Llevas {currentStreak} días consecutivos rescatando comida. 
              Cada día cuenta para reducir el desperdicio alimentario.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreakTracker;